import React, { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { Oval } from "react-loader-spinner";
import { AuthGuard } from "../App";
import { EmailGuard } from "../App";
import { StandardURL } from "../App";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserPlus, faBuilding, faHome, faUser } from "@fortawesome/free-solid-svg-icons";

// --- Role Endpoints (Caretaker removed) ---
const roleEndpoints = {
    business: "auth/registerbusinessnoverification",
    propertyhunter: "auth/registerpropertyhunternoverification",
    tenant: "auth/registercustomernoverification",
};

// --- Role Titles and Icons (Caretaker removed) ---
const roleTitles = {
    business: { title: "Business", icon: faBuilding },
    propertyhunter: { title: "Property Hunter", icon: faHome },
    tenant: { title: "Tenant", icon: faUser },
};

const ManualMultiRoleRegistration = () => {
    // --- formData state (propertyid removed) ---
    const [formData, setFormData] = useState({
        email: "",
        name: "",
        phoneNumber: "",
        password: "",
        confirmPassword: "",
        termsAndCondition: true, // Auto-checked for manual registration
        // propertyid is no longer needed
    });

    const [selectedRole, setSelectedRole] = useState("business"); // Default role set to 'business'
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const token = localStorage.getItem("token");

    // --- useEffect block for propertyId removed ---

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === "checkbox" ? checked : value,
        });
    };

    const handleRoleChange = (role) => {
        setSelectedRole(role);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (formData.password !== formData.confirmPassword) {
            toast.error("Passwords do not match.");
            return;
        }
        
        // --- propertyid validation removed ---

        const endpoint = roleEndpoints[selectedRole];
        if (!endpoint) {
            toast.error("Invalid role selected.");
            return;
        }

        setIsLoading(true);

        // Prepare payload: no propertyid needed, termsAndCondition formatted
        const updatedFormData = {
            ...formData,
            termsAndCondition: formData.termsAndCondition ? "on" : "off",
        };

        // Remove confirmPassword from the actual submission payload
        const { confirmPassword, ...payload } = updatedFormData;


        try {
            const response = await axios.post(
                `${StandardURL}/iNyumba/${endpoint}`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            // Assuming a successful registration gives responseCode 200 or 201
            if (response.data.success) {
                toast.success(
                    `${roleTitles[selectedRole].title} registered successfully!`
                );
                // Clear the form
                setFormData((prev) => ({
                    ...prev,
                    email: "",
                    name: "",
                    phoneNumber: "",
                    password: "",
                    confirmPassword: "",
                }));
            } else {
                toast.error(
                    response.data.response ||
                    `Registration failed: ${roleTitles[selectedRole].title}`
                );
            }
        } catch (error) {
            const errorMessage =
                error.response?.data?.response ||
                error.response?.data?.message ||
                "An unexpected error occurred during registration.";
            toast.error(errorMessage);
            console.error("Registration error:", error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <EmailGuard>
            <AuthGuard>
                <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
                    <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-xl shadow-2xl">
                        <div className="text-center">
                            <h2 className="mt-6 text-3xl font-extrabold text-gray-900 flex items-center justify-center">
                                <FontAwesomeIcon icon={faUserPlus} className="mr-3 text-indigo-600" />
                                Manual User Registration
                            </h2>
                            <p className="mt-2 text-sm text-gray-600">
                                Register a new user without email verification.
                            </p>
                        </div>

                        {/* --- Role Selection Radio Buttons (Caretaker Removed) --- */}
                        <div className="mt-6">
                            <label className="block text-sm font-medium text-gray-700 mb-3">Select Account Type:</label>
                            <div className="grid grid-cols-3 gap-3"> {/* Updated to grid-cols-3 */}
                                {Object.keys(roleTitles).map((role) => (
                                    <label
                                        key={role}
                                        className={`flex flex-col items-center justify-center p-3 border-2 rounded-lg cursor-pointer transition duration-200 ${
                                            selectedRole === role
                                                ? "border-indigo-600 bg-indigo-50 shadow-md text-indigo-700"
                                                : "border-gray-300 bg-white hover:bg-gray-50 text-gray-600"
                                        }`}
                                    >
                                        <input
                                            type="radio"
                                            name="userRole"
                                            value={role}
                                            checked={selectedRole === role}
                                            onChange={() => handleRoleChange(role)}
                                            className="hidden"
                                        />
                                        <FontAwesomeIcon icon={roleTitles[role].icon} className="w-5 h-5 mb-1" />
                                        <span className="text-xs font-semibold text-center">{roleTitles[role].title}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                        {/* ----------------------------------------------------- */}

                        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                            <input type="hidden" name="remember" defaultValue="true" />
                            <div className="rounded-md shadow-sm -space-y-px">
                                
                                {/* Name */}
                                <div>
                                    <label htmlFor="name" className="sr-only">Full Name</label>
                                    <input
                                        id="name"
                                        name="name"
                                        type="text"
                                        required
                                        className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                                        placeholder="Full Name"
                                        value={formData.name}
                                        onChange={handleChange}
                                    />
                                </div>
                                
                                {/* Phone Number */}
                                <div>
                                    <label htmlFor="phoneNumber" className="sr-only">Phone Number</label>
                                    <input
                                        id="phoneNumber"
                                        name="phoneNumber"
                                        type="tel"
                                        required
                                        className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                                        placeholder="Phone Number (e.g., 2547XXXXXXXX)"
                                        value={formData.phoneNumber}
                                        onChange={handleChange}
                                    />
                                </div>

                                {/* Email */}
                                <div>
                                    <label htmlFor="email" className="sr-only">Email address</label>
                                    <input
                                        id="email"
                                        name="email"
                                        type="email"
                                        autoComplete="email"
                                        required
                                        className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                                        placeholder="Email address"
                                        value={formData.email}
                                        onChange={handleChange}
                                    />
                                </div>
                                
                                {/* Password */}
                                <div>
                                    <label htmlFor="password" className="sr-only">Password</label>
                                    <input
                                        id="password"
                                        name="password"
                                        type="password"
                                        autoComplete="new-password"
                                        required
                                        className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                                        placeholder="Password"
                                        value={formData.password}
                                        onChange={handleChange}
                                    />
                                </div>

                                {/* Confirm Password */}
                                <div>
                                    <label htmlFor="confirmPassword" className="sr-only">Confirm Password</label>
                                    <input
                                        id="confirmPassword"
                                        name="confirmPassword"
                                        type="password"
                                        autoComplete="new-password"
                                        required
                                        className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                                        placeholder="Confirm Password"
                                        value={formData.confirmPassword}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>

                            <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                    <input
                                        id="termsAndCondition"
                                        name="termsAndCondition"
                                        type="checkbox"
                                        required
                                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                        checked={formData.termsAndCondition}
                                        onChange={handleChange}
                                    />
                                    <label htmlFor="termsAndCondition" className="ml-2 block text-sm text-gray-900">
                                        I confirm registration details are correct
                                    </label>
                                </div>
                            </div>

                            <div>
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className={`
                                      relative w-full flex justify-center py-2.5 px-4 border border-transparent text-lg font-semibold rounded-lg shadow-md
                                      ${isLoading
                                        ? "bg-indigo-400 cursor-not-allowed"
                                        : "bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transform transition-all duration-200 ease-in-out hover:scale-105"
                                      }
                                      text-white
                                    `}
                                >
                                    {isLoading ? (
                                        <Oval
                                            height={24}
                                            width={24}
                                            color="white"
                                            visible={true}
                                            ariaLabel="loading-indicator"
                                            secondaryColor="white"
                                            strokeWidth={5}
                                            strokeWidthSecondary={5}
                                        />
                                    ) : (
                                        `Register ${roleTitles[selectedRole].title}`
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </AuthGuard>
        </EmailGuard>
    );
};

export default ManualMultiRoleRegistration;